import React, { useCallback, useMemo, useState } from "react";

import type { OutgoingIntakeAnswer } from "../../../api/types/outgoingIntake";
import { CitationDocumentsProvider } from "../context/CitationDocumentsContext";
import { CitationViewerProvider } from "../context/CitationViewerContext";
import { useCaseyChat } from "../hooks/useCaseyChat";
import type { CaseyProposal, IntakeControlsLayout } from "../types";
import { CaseyLauncher } from "./CaseyLauncher";
import { CaseyPanel } from "./CaseyPanel";

type Props = {
  token: string;
  answers: OutgoingIntakeAnswer[];
  /** Writes an accepted suggestion into the form. */
  onApplyProposal: (proposal: CaseyProposal) => void;
  activeQuestionId?: string | null;
  attorneyName?: string | null;
  lawFirmName?: string | null;
  /** The page background is firm-branded, so the launcher has to adapt to it. */
  isDarkBg: boolean;
  /**
   * What the intake's bottom-right controls are showing, so the launcher can sit
   * to their left without overlapping them.
   */
  intakeControls: IntakeControlsLayout;
  assistantName?: string;
};

/**
 * Ask Casey: the launcher and panel, plus the chat state that outlives the panel
 * being closed. Mounted by the public intake page; it deliberately owns no form
 * state, reaching the form only through `onApplyProposal`.
 */
export function AskCasey({
  token,
  answers,
  onApplyProposal,
  activeQuestionId,
  attorneyName,
  lawFirmName,
  isDarkBg,
  intakeControls,
  assistantName = "Casey"
}: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const chat = useCaseyChat({
    token,
    answers,
    onAcceptProposal: onApplyProposal,
    activeQuestionId
  });

  // Only count suggestions the client hasn't ruled on, and only while the panel
  // is shut — a badge over an open panel is just noise.
  const pendingCount = useMemo(() => {
    if (isOpen) return 0;
    return chat.proposals.filter(
      (proposal) =>
        (chat.decisions[proposal.proposal_id] ?? "pending") === "pending"
    ).length;
  }, [isOpen, chat.proposals, chat.decisions]);

  const handleSend = useCallback(
    (text: string) => {
      setIsOpen(true);
      chat.sendMessage(text);
    },
    [chat]
  );

  if (!token) return null;

  return (
    <CitationDocumentsProvider token={token}>
      <CitationViewerProvider isChatOpen={isOpen}>
        <CaseyLauncher
          onClick={() => setIsOpen((open) => !open)}
          isDarkBg={isDarkBg}
          badgeCount={pendingCount}
          label={`Ask ${assistantName}`}
          intakeControls={intakeControls}
        />
        <CaseyPanel
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          assistantName={assistantName}
          attorneyName={attorneyName}
          lawFirmName={lawFirmName}
          messages={chat.messages}
          isStreaming={chat.isStreaming}
          isRestoring={chat.isRestoring}
          error={chat.error}
          onClearError={chat.clearError}
          onSend={handleSend}
          onStop={chat.stop}
          attachments={chat.attachments}
          onUpload={chat.uploadAttachment}
          uploadError={chat.uploadError}
          decisions={chat.decisions}
          onAcceptProposal={chat.acceptProposal}
          onRejectProposal={chat.rejectProposal}
          onAcceptAll={chat.acceptAll}
        />
      </CitationViewerProvider>
    </CitationDocumentsProvider>
  );
}
